import { PlayArrow as PlayIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { apiRequest } from '../../../utils/api';

interface Tool {
  toolId: number;
  toolName: string;
  description: string;
  category: string;
  isActive: boolean;
  timeoutSeconds: number;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
  lastExecutedAt: string;
}

interface ToolTestModalProps {
  open: boolean;
  tool: Tool | null;
  onClose: () => void;
}

interface TestResult {
  success: boolean;
  result?: any;
  error?: string;
  executionTime?: number;
  logs?: string[];
}

const ToolTestModal: React.FC<ToolTestModalProps> = ({ open, tool, onClose }) => {
  const [input, setInput] = useState('');
  const [parameters, setParameters] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const handleTest = async () => {
    if (!tool) return;

    try {
      setTesting(true);
      setTestResult(null);

      let parsedParams = {};
      if (parameters.trim()) {
        try {
          parsedParams = JSON.parse(parameters);
        } catch (e) {
          setTestResult({
            success: false,
            error: 'JSON 형식이 올바르지 않습니다.',
          });
          setTesting(false);
          return;
        }
      }

      const response = await apiRequest.post(`/api/v1/apps/ai-manager/super-admin/tools/${tool.toolId}/test`, {
        input,
        parameters: parsedParams
      });

      setTestResult(response.data || response);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || '테스트 실행 중 오류가 발생했습니다.',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    setInput('');
    setParameters('');
    setTestResult(null);
    onClose();
  };

  const renderTestResult = () => {
    if (!testResult) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">실행 결과</Typography>
        </Divider>
        
        {testResult.success ? (
          <Alert severity="success">
            <Typography variant="body2" fontWeight="bold">테스트 성공</Typography>
            {testResult.executionTime && (
              <Typography variant="body2">실행 시간: {testResult.executionTime}ms</Typography>
            )}
            {testResult.result && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">결과:</Typography>
                <Box 
                  component="pre"
                  sx={{ 
                    bgcolor: 'grey.100', 
                    p: 1, 
                    borderRadius: 1,
                    mt: 1,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 200
                  }}
                >
                  {typeof testResult.result === 'object' 
                    ? JSON.stringify(testResult.result, null, 2)
                    : testResult.result
                  }
                </Box>
              </Box>
            )}
          </Alert>
        ) : (
          <Alert severity="error">
            <Typography variant="body2" fontWeight="bold">테스트 실패</Typography>
            <Typography variant="body2">{testResult.error}</Typography>
          </Alert>
        )}

        {testResult.logs && testResult.logs.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">실행 로그:</Typography>
            <Box 
              sx={{ 
                bgcolor: 'grey.50', 
                p: 1, 
                borderRadius: 1,
                mt: 1,
                maxHeight: 200,
                overflow: 'auto'
              }}
            >
              {testResult.logs.map((log, index) => (
                <Typography 
                  key={index} 
                  variant="body2" 
                  component="div"
                  sx={{ fontSize: 12, fontFamily: 'monospace' }}
                >
                  {log}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <PlayIcon sx={{ mr: 1 }} />
          Tool 테스트: {tool?.toolName}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {tool && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>설명:</strong> {tool.description}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography variant="body1">
                  <strong>카테고리:</strong>
                </Typography>
                <Chip label={tool.category} size="small" />
              </Box>
              <Typography variant="body1">
                <strong>타임아웃:</strong> {tool.timeoutSeconds}초
              </Typography>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="테스트 입력"
              placeholder="Tool에 전달할 입력 데이터를 작성하세요..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="추가 매개변수 (JSON)"
              placeholder='{"key": "value"}'
              helperText='JSON 형식으로 추가 매개변수를 입력하세요. 예: {"timeout": 30, "retries": 3}'
              value={parameters}
              onChange={(e) => setParameters(e.target.value)}
            />

            {testing && (
              <Box display="flex" flexDirection="column" alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Tool 실행 중...
                </Typography>
              </Box>
            )}

            {renderTestResult()}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          닫기
        </Button>
        <Button 
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleTest}
          disabled={testing || !input.trim()}
        >
          실행
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToolTestModal;
